import React, { useState, useRef, DragEvent } from 'react';
import axios from 'axios';
import { 
  Box, Button, Typography, Paper, LinearProgress, 
  FormControlLabel, Checkbox, Alert, useTheme,
  Modal, IconButton, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { UploadResponse } from '../types';

interface FileUploadProps {
  onUploadComplete: (data: UploadResponse) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({ onUploadComplete }) => {
  const theme = useTheme();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isShopQL, setIsShopQL] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [shopQLHelpOpen, setShopQLHelpOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('isShopQL', isShopQL.toString());

    try {
      const response = await axios.post<UploadResponse>('/api/upload', formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 100));
          setUploadProgress(percentCompleted);
        },
      });

      onUploadComplete(response.data);
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      
      if (fileExtension === 'xlsx' || fileExtension === 'csv') {
        setSelectedFile(file);
        setError(null);
      } else {
        setError('Only XLSX and CSV files are allowed');
      }
    }
  };

  const handleOpenShopQLHelp = () => {
    setShopQLHelpOpen(true);
  };

  const handleCloseShopQLHelp = () => {
    setShopQLHelpOpen(false);
    setCopySuccess(false);
  };

  const shopQLQuery = `SHOW order_id, net_sales AS net_total, total_sales AS order_total, 
     taxes AS sales_tax, total_shipping_charges AS shipping,
     shipping_region as shipping_state, day as order_date
GROUP BY order_id, shipping_state, order_date
WITH TOTALS
SINCE [YYYY-MM-DD]
UNTIL [YYYY-MM-DD]
ORDER BY order_date ASC`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shopQLQuery)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });
  };

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="h2" gutterBottom>
        Upload Your Data File
      </Typography>

      <Paper 
        elevation={0} 
        sx={{ 
          p: 5, 
          border: `2px dashed ${isDragging ? theme.palette.primary.main : '#ccc'}`,
          backgroundColor: isDragging ? 'rgba(80, 117, 211, 0.05)' : '#fafafa',
          mb: 3,
          transition: 'all 0.2s ease',
          cursor: 'pointer'
        }}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          accept=".xlsx,.csv"
          style={{ display: 'none' }}
          id="raised-button-file"
          type="file"
          onChange={handleFileChange}
          disabled={uploading}
        />
        
        <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        
        <Typography variant="h6" gutterBottom>
          {isDragging ? 'Drop file here' : 'Drag & drop a file here'}
        </Typography>
        
        <Typography variant="body1" sx={{ mt: 1 }}>
          {selectedFile ? `Selected: ${selectedFile.name}` : 'Or click to browse files'}
        </Typography>
        
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Supported formats: XLSX, CSV
        </Typography>
      </Paper>

      <FormControlLabel
        control={
          <Checkbox
            checked={isShopQL}
            onChange={(e) => setIsShopQL(e.target.checked)}
            disabled={uploading}
          />
        }
        label={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            This is a ShopQL export file (auto-maps columns)
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleOpenShopQLHelp();
              }}
              sx={{ ml: 1 }}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        }
      />

      {/* ShopQL Help Dialog */}
      <Dialog
        open={shopQLHelpOpen}
        onClose={handleCloseShopQLHelp}
        maxWidth="md"
      >
        <DialogTitle>
          <Typography variant="h6">
            ShopQL Query Format
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Here is the Shopify Analyzer Query to use! Make sure you change the 'SINCE' (begin) date and 'UNTIL' (end) date properties:
          </Typography>
          <Box
            sx={{
              bgcolor: 'grey.100',
              p: 2,
              borderRadius: 1,
              fontFamily: 'monospace',
              position: 'relative',
              whiteSpace: 'pre',
              overflow: 'auto'
            }}
          >
            {shopQLQuery}
            <IconButton
              onClick={copyToClipboard}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'background.paper',
                '&:hover': {
                  bgcolor: 'background.paper',
                }
              }}
              size="small"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            {copySuccess && (
              <Typography 
                variant="caption" 
                sx={{ 
                  position: 'absolute', 
                  top: 12, 
                  right: 42,
                  color: 'success.main'
                }}
              >
                Copied!
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseShopQLHelp}>Close</Button>
        </DialogActions>
      </Dialog>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {uploading && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="body2" sx={{ mt: 1 }}>
            Uploading: {uploadProgress}%
          </Typography>
        </Box>
      )}

      <Button
        variant="contained"
        color="primary"
        onClick={handleUpload}
        disabled={!selectedFile || uploading}
        sx={{ mt: 3 }}
      >
        Upload and Continue
      </Button>

      <Box sx={{ mt: 4, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Important Requirements
        </Typography>
        <ul style={{ textAlign: 'left' }}>
          <li>Input files must be in XLSX or CSV format with no formulas or macros</li>
          <li>Data must include state and date columns</li>
          <li>For accurate analysis, ensure data is for consecutive years</li>
        </ul>
      </Box>
    </Box>
  );
};

export default FileUpload;